import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';

/**
 * Handles incoming shares from the Web Share Target API.
 * This is triggered when users share content TO the Read l8r app.
 *
 * URL: /app/share
 * Receives: title, text, url via POST or query params
 */
export function ShareReceiverPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [status, setStatus] = useState<'loading' | 'saving' | 'success' | 'error' | 'auth-required'>('loading');
  const [message, setMessage] = useState('');
  const [articleTitle, setArticleTitle] = useState('');

  // Extract shared data from URL params or POST body
  const sharedTitle = searchParams.get('title') || '';
  const sharedText = searchParams.get('text') || '';
  const sharedUrl = searchParams.get('url') || extractUrlFromText(sharedText);

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      // Save the share intent and redirect to auth
      sessionStorage.setItem('pending_share', JSON.stringify({
        title: sharedTitle,
        text: sharedText,
        url: sharedUrl
      }));
      setStatus('auth-required');
      return;
    }

    // User is authenticated, proceed to save
    saveArticle();
  }, [user, authLoading, sharedUrl]);

  async function saveArticle() {
    if (!sharedUrl) {
      setStatus('error');
      setMessage('No URL found in shared content');
      return;
    }

    setStatus('saving');
    setArticleTitle(sharedTitle || sharedUrl);

    try {
      // Call the save article function
      const { data, error } = await supabase.functions.invoke('save-article', {
        body: {
          url: sharedUrl,
          title: sharedTitle || undefined
        }
      });

      if (error) throw error;

      setStatus('success');
      setArticleTitle(data.title || sharedTitle || sharedUrl);
      setMessage('Article saved to your library!');

      // Redirect to the article after a brief delay
      setTimeout(() => {
        navigate(`/app#article/${data.id}`, { replace: true });
      }, 1500);
    } catch (err) {
      setStatus('error');
      setMessage(err instanceof Error ? err.message : 'Failed to save article');
    }
  }

  function handleSignIn() {
    // Redirect to app with auth, the pending share will be handled after login
    navigate('/app', { replace: true });
  }

  function handleCancel() {
    sessionStorage.removeItem('pending_share');
    navigate('/app', { replace: true });
  }

  return (
    <div className="share-receiver">
      <div className="share-card">
        {status === 'loading' && (
          <>
            <div className="spinner" />
            <p>Preparing to save...</p>
          </>
        )}

        {status === 'auth-required' && (
          <>
            <div className="share-icon">📥</div>
            <h2>Sign in to save</h2>
            <p className="share-preview">
              {sharedTitle || sharedUrl || 'Shared content'}
            </p>
            <div className="share-actions">
              <button className="btn-primary" onClick={handleSignIn}>
                Sign In
              </button>
              <button className="btn-secondary" onClick={handleCancel}>
                Cancel
              </button>
            </div>
          </>
        )}

        {status === 'saving' && (
          <>
            <div className="spinner" />
            <h2>Saving article...</h2>
            <p className="share-preview">{articleTitle}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="success-icon">✓</div>
            <h2>Saved!</h2>
            <p className="share-preview">{articleTitle}</p>
            <p className="redirect-notice">Opening article...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="error-icon">⚠️</div>
            <h2>Couldn't save</h2>
            <p className="error-message">{message}</p>
            <div className="share-actions">
              <button className="btn-primary" onClick={() => saveArticle()}>
                Try Again
              </button>
              <button className="btn-secondary" onClick={handleCancel}>
                Cancel
              </button>
            </div>
          </>
        )}
      </div>

      <style>{`
        .share-receiver {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1.5rem;
          background: var(--bg-primary, #0a0a0a);
          color: var(--text-primary, #fafaf9);
        }

        .share-card {
          background: var(--bg-secondary, #141414);
          border: 1px solid var(--border, #27272a);
          border-radius: 16px;
          padding: 2rem;
          text-align: center;
          max-width: 400px;
          width: 100%;
        }

        .share-icon,
        .success-icon,
        .error-icon {
          font-size: 3rem;
          margin-bottom: 1rem;
        }

        .success-icon {
          width: 64px;
          height: 64px;
          background: #22c55e;
          color: white;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 2rem;
        }

        h2 {
          font-size: 1.25rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }

        .share-preview {
          color: var(--text-secondary, #a1a1aa);
          font-size: 0.875rem;
          margin-bottom: 1.5rem;
          word-break: break-word;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .redirect-notice {
          color: var(--text-secondary, #a1a1aa);
          font-size: 0.875rem;
          margin-top: 0.5rem;
        }

        .error-message {
          color: #ef4444;
          font-size: 0.875rem;
          margin-bottom: 1.5rem;
        }

        .share-actions {
          display: flex;
          gap: 0.75rem;
          justify-content: center;
        }

        .spinner {
          width: 40px;
          height: 40px;
          border: 3px solid rgba(249, 115, 22, 0.2);
          border-top-color: #f97316;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          margin: 0 auto 1rem;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .btn-primary {
          background: #f97316;
          color: white;
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          font-weight: 600;
          border: none;
          cursor: pointer;
          transition: background 0.2s;
        }

        .btn-primary:hover {
          background: #ea580c;
        }

        .btn-secondary {
          background: transparent;
          color: var(--text-primary, #fafaf9);
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          font-weight: 600;
          border: 1px solid var(--border, #27272a);
          cursor: pointer;
          transition: background 0.2s;
        }

        .btn-secondary:hover {
          background: var(--bg-tertiary, #1f1f1f);
        }
      `}</style>
    </div>
  );
}

/**
 * Extract URL from shared text (some apps include URL in text field)
 */
function extractUrlFromText(text: string): string {
  if (!text) return '';

  // Match URLs in the text
  const urlMatch = text.match(/https?:\/\/[^\s]+/);
  return urlMatch ? urlMatch[0] : '';
}
